import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Asset } from 'components/common/types/Asset.types';
import { Location, searchLocation } from 'modules/api/locationSearch';
import { AsyncSelectLoadDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import {
  getSharedCampaignPlanAssets,
  getUnavailableSharedCampaignPlanAssets,
} from 'store/sharedCampaignPlan/selectors';
import {
  CombinedSearchResult,
  LocationSearchResult,
  AssetSearchResult,
} from 'components/pages/Planner/PlannerMap/SearchBar/SearchBar.types';

const MIN_QUERY_LENGTH = 3;
const MAX_ASSET_RESULTS = 10;

const normalizeLocation = (location: Location): LocationSearchResult => ({
  type: 'location',
  displayText: location.display_name,
  value: location.place_id.toString(),
  latitude: parseFloat(location.lat),
  longitude: parseFloat(location.lon),
  originalData: location,
});

const normalizeAsset = (asset: Asset): AssetSearchResult => ({
  type: 'asset',
  displayText: asset.routeFrameCode,
  value: asset.routeFrameCode,
  latitude: asset.latitude,
  longitude: asset.longitude,
  originalData: asset,
});

export const useCampaignPlanSearchData = (): {
  loadCombinedResults: AsyncSelectLoadDataHandler<CombinedSearchResult>;
} => {
  const filteredAssets = useSelector(getSharedCampaignPlanAssets) as Asset[];
  const missingAssets = useSelector(getUnavailableSharedCampaignPlanAssets) as Asset[];
  const allAssets = useMemo(() => [...filteredAssets, ...missingAssets], [filteredAssets, missingAssets]);

  const searchAssets = (inputValue: string): Asset[] => {
    const query = inputValue.toLowerCase();

    return allAssets
      .filter(
        (asset) => asset.routeFrameCode?.toLowerCase().includes(query) || asset.frameId?.toLowerCase().includes(query),
      )
      .slice(0, MAX_ASSET_RESULTS);
  };

  const loadLocations = async (inputValue: string): Promise<Location[]> => {
    try {
      return await searchLocation(inputValue);
    } catch {
      return [];
    }
  };

  const loadCombinedResults = async (_: string, inputValue: string): Promise<CombinedSearchResult[]> => {
    if (inputValue.length < MIN_QUERY_LENGTH) return [];

    const locations = await loadLocations(inputValue);
    const normalizedLocations: LocationSearchResult[] = locations.map(normalizeLocation);

    const matchedAssets = searchAssets(inputValue);
    const normalizedAssets: AssetSearchResult[] = matchedAssets.map(normalizeAsset);

    return [...normalizedAssets, ...normalizedLocations];
  };

  return { loadCombinedResults };
};
